/**
 * Paper prescriptions (NEW-WF-12, module id `rx`, routes under `/v1/rx`).
 * Library: imaging-docs MM-14 (paper Rx), MM-15 (eRx).
 *
 * Printing makes a PDF filed to the patient's Documents (kind `lab_rx`). A
 * printed prescription is never deleted: Void needs a reason and records the
 * actor. Electronic prescribing and EPCS (DoseSpot) are not available: the
 * vendor decision is tracked in INF-04, and `erx` says so.
 */
import { z } from 'zod';

const Id = z.uuid();

export const RX_PERMISSIONS = {
  view: 'chart.view',
  /** Write and print prescriptions (clinicians who sign notes). */
  print: 'notes.sign',
  /** Edit the practice's Rx templates. */
  manage: 'settings.manage',
  /**
   * Print with another dentist as the prescriber (owner and office manager;
   * dentist role test 2026-10-20, finding 21). Audited `rx.print_for_other`.
   */
  forOthers: 'notes.rx_for_others',
  /**
   * Prepare a draft for the dentist to sign (assistants; owner feedback
   * 2026-10-21: "assistants can draft"). Drafts never print.
   */
  draft: 'chart.edit',
} as const;

export const DeaScheduleSchema = z.enum(['II', 'III', 'IV', 'V']);

export const RxItemSchema = z.object({
  drug: z.string().trim().min(1).max(120),
  strength: z.string().trim().max(60).nullable().optional(),
  form: z.string().trim().max(40).nullable().optional(),
  sig: z.string().trim().min(1).max(500),
  dispense: z.string().trim().min(1).max(80),
  refills: z.number().int().min(0).max(11),
  deaSchedule: DeaScheduleSchema.nullable().optional(),
  substitutionAllowed: z.boolean().optional(),
});
export type RxItemInput = z.infer<typeof RxItemSchema>;

export const RxTemplateSchema = RxItemSchema.extend({
  id: Id,
  name: z.string(),
  note: z.string().nullable(),
  active: z.boolean(),
});
export type RxTemplate = z.infer<typeof RxTemplateSchema>;
export const RxTemplateBodySchema = RxItemSchema.extend({
  name: z.string().trim().min(1).max(120),
  note: z.string().trim().max(500).nullable().optional(),
  active: z.boolean().optional(),
});

export const RxPrescriberSchema = z.object({
  id: Id,
  name: z.string(),
  npi: z.string().nullable(),
  hasDea: z.boolean(),
  /** Last 4 of the DEA number, for the setup page (never the whole number). */
  deaLast4: z.string().nullable().optional(),
  stateLicense: z.string().nullable().optional(),
});

/** Prescriber setup: DEA number and state license printed on paper Rx. */
export const RxPrescriberBodySchema = z
  .object({
    /** Two letters and seven digits; null clears it. */
    dea: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}[0-9]{7}$/, 'A DEA number is two letters and seven digits.')
      .nullable()
      .optional(),
    stateLicense: z.string().trim().max(40).nullable().optional(),
  })
  .strict();
export type RxPrescriberBody = z.input<typeof RxPrescriberBodySchema>;

export const RxSetupSchema = z.object({
  templates: z.array(RxTemplateSchema),
  prescribers: z.array(RxPrescriberSchema),
  canPrint: z.boolean(),
  canManage: z.boolean(),
  erx: z.object({ available: z.literal(false), reason: z.string() }),
  /** `chart.edit`: may save a draft for a dentist to sign. */
  canDraft: z.boolean().optional(),
  /** The signed-in dentist: the prescriber by default (dentist role test, finding 21). */
  myProviderId: Id.nullable().optional(),
  /** `notes.rx_for_others`: may print with another dentist as the prescriber (audited). */
  canPrintForOthers: z.boolean().optional(),
});
export type RxSetup = z.infer<typeof RxSetupSchema>;

/**
 * The prescribers a user may pick (pure; shared by the page and the API):
 * their own dentist record, plus every dentist with `notes.rx_for_others`.
 * Staff who are not dentists and lack the right pick none (they cannot print).
 */
export function pickablePrescribers<T extends { id: string }>(
  prescribers: readonly T[],
  myProviderId: string | null | undefined,
  canPrintForOthers: boolean,
): T[] {
  if (canPrintForOthers) return [...prescribers];
  return prescribers.filter((p) => p.id === myProviderId);
}

export const RxStatusSchema = z.enum(['draft', 'printed', 'void']);
export type RxStatus = z.infer<typeof RxStatusSchema>;

export const PrescriptionSchema = z.object({
  id: Id,
  patientId: Id,
  locationId: Id,
  providerId: Id,
  providerName: z.string(),
  items: z.array(RxItemSchema),
  /** `draft`: prepared for the dentist to sign, no PDF yet. */
  status: RxStatusSchema,
  /** Null for a draft. */
  printedAt: z.string().nullable(),
  printedByName: z.string().nullable(),
  documentId: Id.nullable(),
  note: z.string().nullable(),
  voidReason: z.string().nullable(),
  voidedAt: z.string().nullable(),
  voidedByName: z.string().nullable(),
  createdAt: z.string().optional(),
  draftedByName: z.string().nullable().optional(),
  /** Any line is a controlled substance (prints for a wet signature only). */
  controlled: z.boolean().optional(),
  /** Warning keys the signer acknowledged (`allergy:penicillin`, ...). */
  acknowledgedWarnings: z.array(z.string()).optional(),
  /** The office-wide list names the patient (it is a PHI read, audited). */
  patientName: z.string().nullable().optional(),
  clinicalNoteId: Id.nullable().optional(),
});
export type Prescription = z.infer<typeof PrescriptionSchema>;
export const PMS_RX_SYSTEMS = ['dentrix', 'opendental', 'eaglesoft'] as const;

/**
 * A prescription written in the practice management system (Dentrix
 * admin.rxrec, Open Dental rxpat, Eaglesoft PPM.prescriptions), synced
 * read-only through PulseSync (`pms_prescription`). Quantity and refills are
 * the PMS's own text ("#21", "PRN"); `pmsStatus` is Eaglesoft's only.
 */
export const PmsPrescriptionSchema = z.object({
  id: Id,
  patientId: Id,
  locationId: Id,
  pmsSystem: z.enum(PMS_RX_SYSTEMS),
  /** YYYY-MM-DD, office-local; null when the PMS has no date. */
  rxDate: z.string().nullable(),
  drug: z.string(),
  /** The PMS's own label when it differs from the drug (Dentrix "Amoxicillin 500mg"). */
  description: z.string().nullable(),
  sig: z.string().nullable(),
  quantity: z.string().nullable(),
  refills: z.string().nullable(),
  daysSupply: z.number().int().nullable(),
  prescriberName: z.string().nullable(),
  pharmacyName: z.string().nullable(),
  pharmacyPhone: z.string().nullable(),
  controlled: z.boolean().nullable(),
  dispenseAsWritten: z.boolean().nullable(),
  expiresOn: z.string().nullable(),
  pmsStatus: z.string().nullable(),
  note: z.string().nullable(),
});
export type PmsPrescription = z.infer<typeof PmsPrescriptionSchema>;

/**
 * Whether the patient's PMS prescriptions reach Velora: `synced` once the
 * office's PulseSync prescriptions read has run, `not_synced` before (the
 * list then says so instead of reading as "no history"), `none` for a patient
 * Velora holds alone.
 */
export const PmsRxFeedSchema = z.object({
  state: z.enum(['synced', 'not_synced', 'none']),
  /** "Dentrix", "Open Dental", "Eaglesoft"; null for `none`. */
  pmsName: z.string().nullable(),
});
export type PmsRxFeed = z.infer<typeof PmsRxFeedSchema>;

export const PrescriptionListSchema = z.object({
  prescriptions: z.array(PrescriptionSchema),
  /** More match than were returned (narrow the filters). */
  truncated: z.boolean().optional(),
  /** One patient's list only: the prescriptions written in the PMS, newest first. */
  pmsHistory: z.array(PmsPrescriptionSchema).optional(),
  pmsFeed: PmsRxFeedSchema.optional(),
});
export type PrescriptionList = z.infer<typeof PrescriptionListSchema>;

/** The list: one patient's history, or the office's recent prescriptions with filters. */
export const RxListQuerySchema = z.object({
  patientId: z.uuid().optional(),
  status: z.enum(['draft', 'printed', 'void', 'all']).default('all'),
  providerId: z.uuid().optional(),
  /** YYYY-MM-DD, in the office's zone (printed date, or created date for drafts). */
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  controlled: z.enum(['true', 'false']).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type RxListQuery = z.input<typeof RxListQuerySchema>;

const AckSchema = z.array(z.string().trim().min(1).max(80)).max(30);

export const PrintRxBodySchema = z.object({
  patientId: Id,
  providerId: Id,
  items: z.array(RxItemSchema).min(1).max(6),
  note: z.string().trim().max(500).nullable().optional(),
  /**
   * Allergy and alert warning keys the prescriber saw and accepted. An
   * allergy match that is not listed here is refused (409, `rx_allergy`).
   */
  acknowledgedWarnings: AckSchema.optional(),
});

/** Save or update a draft (assistants and dentists); the prescriber signs it later. */
export const DraftRxBodySchema = z.object({
  patientId: Id,
  providerId: Id,
  items: z.array(RxItemSchema).min(1).max(6),
  note: z.string().trim().max(500).nullable().optional(),
});
export type DraftRxBody = z.input<typeof DraftRxBodySchema>;

/** Sign a draft: prints it to Documents and writes the clinical note line. */
export const SignRxBodySchema = z.object({ acknowledgedWarnings: AckSchema.optional() });
export const VoidRxBodySchema = z.object({ reason: z.string().trim().min(3).max(500) });

/**
 * Shown whenever a controlled substance is on the prescription: Velora
 * prints paper prescriptions only and cannot send EPCS.
 */
export const EPCS_UNSUPPORTED =
  'Controlled substance: Velora cannot send electronic prescriptions for controlled substances (EPCS). This prints on paper for a wet signature by the prescriber. Several states require controlled-substance prescriptions to be sent electronically; check your state rules before handing this to the patient.';

export const ERX_UNAVAILABLE =
  'Electronic prescribing (DoseSpot, including EPCS) is not set up yet: the vendor decision is pending (INF-04). Print the prescription and sign it.';

/**
 * The rule shown on the Write Rx dialog and the module page: a controlled
 * substance prints for the prescriber's handwritten signature only.
 */
export const CONTROLLED_RULE =
  'Controlled substances (C-II to C-V) print and wet-sign only: Velora has no EPCS, so the prescriber signs the paper copy by hand with their DEA number on it. No refills on C-II; at most 5 on C-III to C-V. Only the prescriber, signed in as themselves, can print one.';

/** What sending to a pharmacy would take (shown under "Electronic prescribing: coming"). */
export const ERX_OPTIONS: readonly { name: string; detail: string }[] = [
  {
    name: 'DoseSpot',
    detail:
      'Embedded eRx and EPCS widget built for dental software; Surescripts-certified; per-prescriber monthly fee. The vendor INF-04 is evaluating.',
  },
  {
    name: 'DrFirst (Rcopia)',
    detail:
      'eRx and EPCS by API or embedded screens; Surescripts-certified; used by several dental PMSs.',
  },
  {
    name: 'ScriptSure (DAW Systems)',
    detail: 'Embeddable eRx and EPCS with a dental focus; Surescripts-certified.',
  },
  {
    name: 'Direct Surescripts certification',
    detail:
      'Velora certifies its own eRx: months of certification work, plus a DEA-audited EPCS build (identity proofing, two-factor signing, logical access control).',
  },
];

export const ERX_COMING =
  'Electronic prescribing to pharmacies is coming. It needs a certified eRx vendor (such as DoseSpot); until then every prescription prints for the prescriber to sign.';

/**
 * One line of a patient's Rx history: a drug on a Velora prescription (one
 * line per item) or a PMS prescription. The patient's Medical tab lists these
 * (`GET /v1/rx/history`), newest first.
 */
export const RxHistoryEntrySchema = z.object({
  /** `velora`: printed or drafted in Velora (`id` = prescription id); `pms`: from the PMS. */
  source: z.enum(['velora', 'pms']),
  id: Id,
  /** YYYY-MM-DD, office-local. */
  date: z.string().nullable(),
  drug: z.string(),
  sig: z.string().nullable(),
  quantity: z.string().nullable(),
  refills: z.string().nullable(),
  prescriberName: z.string().nullable(),
  /** Velora: draft / printed / void; PMS: `pms`. */
  status: z.enum(['draft', 'printed', 'void', 'pms']),
  pmsSystem: z.enum(PMS_RX_SYSTEMS).nullable(),
  controlled: z.boolean().nullable(),
});
export type RxHistoryEntry = z.infer<typeof RxHistoryEntrySchema>;

export const RxHistoryQuerySchema = z.object({
  patientId: z.uuid(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
});

export const RxHistorySchema = z.object({
  entries: z.array(RxHistoryEntrySchema),
  /** Velora and PMS prescriptions together: how many there are in all. */
  total: z.number().int(),
  pmsFeed: PmsRxFeedSchema,
});
export type RxHistory = z.infer<typeof RxHistorySchema>;

/** YYYY-MM-DD of an instant in the office's zone (pure). */
export function rxLocalDate(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', { timeZone });
}

/**
 * Velora and PMS prescriptions as one history, newest first (pure; shared by
 * the API and the Rx page). A Velora prescription gives one line per drug;
 * a draft is dated by when it was made. Undated PMS rows go last. Ties keep
 * Velora first (it is the newer record of the same visit).
 */
export function mergeRxHistory(
  velora: readonly Pick<
    Prescription,
    'id' | 'items' | 'status' | 'printedAt' | 'createdAt' | 'providerName' | 'controlled'
  >[],
  pms: readonly PmsPrescription[],
  timeZone: string,
): RxHistoryEntry[] {
  const out: (RxHistoryEntry & { order: number })[] = [];
  let order = 0;
  for (const p of velora) {
    const date = rxLocalDate(p.printedAt ?? p.createdAt ?? null, timeZone);
    for (const i of p.items) {
      out.push({
        source: 'velora',
        id: p.id,
        date,
        drug: `${i.drug}${i.strength ? ` ${i.strength}` : ''}`,
        sig: i.sig,
        quantity: i.dispense,
        refills: String(i.refills),
        prescriberName: p.providerName,
        status: p.status,
        pmsSystem: null,
        controlled: i.deaSchedule ? true : (p.controlled ?? null),
        order: order++,
      });
    }
  }
  for (const r of pms) {
    out.push({
      source: 'pms',
      id: r.id,
      date: r.rxDate,
      drug:
        r.description && !r.description.startsWith(r.drug)
          ? `${r.drug} (${r.description})`
          : (r.description ?? r.drug),
      sig: r.sig,
      quantity: r.quantity,
      refills: r.refills,
      prescriberName: r.prescriberName,
      status: 'pms',
      pmsSystem: r.pmsSystem,
      controlled: r.controlled,
      order: order++,
    });
  }
  out.sort((a, b) => {
    if (a.date !== b.date) {
      if (a.date === null) return 1;
      if (b.date === null) return -1;
      return a.date < b.date ? 1 : -1;
    }
    if (a.source !== b.source) return a.source === 'velora' ? -1 : 1;
    return a.order - b.order;
  });
  return out.map(({ order: _o, ...e }) => e);
}

/** "Dentrix" / "Open Dental" / "Eaglesoft". */
export const PMS_RX_NAME: Readonly<Record<(typeof PMS_RX_SYSTEMS)[number], string>> = {
  dentrix: 'Dentrix',
  opendental: 'Open Dental',
  eaglesoft: 'Eaglesoft',
};
